// CoSearch sub-features catalogue
import { Search, UserCheck, GitBranch, Bell, BarChart3, Code2 } from "lucide-react";

const IMG = {
  doc: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  team: "https://images.unsplash.com/photo-1573164713988-8665fc963095?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  legal: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  desk: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  pe: "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  laptop: "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

export const COSEARCH_FEATURES = [
  {
    slug: "company-search", kind: "feature", Icon: Search,
    eyebrow: "Company search", title: "Search 5 million UK companies", highlight: "in seconds.",
    subhead: "Status, address, accounts, filings, charges, every Companies House data point, instantly retrievable.",
    intro: "CoSearch's company search returns a complete UK company profile from a name or number, registered office, SIC codes, accounts size, filing history, charges, officers and PSCs. Aliases, former names and dissolved companies are all indexed.",
    image: IMG.doc,
    subFeatures: [
      { Icon: Search, t: "Fuzzy name search", d: "Find companies by partial name, former name or alias." },
      { Icon: Search, t: "Number lookup", d: "Direct retrieval by 8-digit Companies House number." },
      { Icon: Search, t: "Active + dissolved", d: "Index covers every UK company since 1844." },
      { Icon: Search, t: "Full record retrieval", d: "Officers, PSCs, charges, accounts, all in one fetch." },
    ],
    related: ["director-search", "ownership-map", "watchlists"],
  },
  {
    slug: "director-search", kind: "feature", Icon: UserCheck,
    eyebrow: "Director & PSC search", title: "Search every UK director", highlight: "and PSC.",
    subhead: "Find every appointment, resignation and disqualification across UK companies, by name, DOB or address.",
    intro: "CoSearch indexes every UK director, secretary and Person with Significant Control. Find every company a person has been involved with, plus disqualifications, current appointments and historical roles, perfect for AML, conflict checks and counterparty research.",
    image: IMG.team,
    subFeatures: [
      { Icon: UserCheck, t: "Officer history", d: "Every appointment and resignation, current and historical." },
      { Icon: UserCheck, t: "Disqualified directors", d: "UK disqualification register integrated." },
      { Icon: UserCheck, t: "PSC ownership trail", d: "Trace beneficial ownership across multiple companies." },
      { Icon: UserCheck, t: "Conflict checks", d: "Cross-reference director appointments against your client list." },
    ],
    related: ["company-search", "ownership-map", "watchlists"],
  },
  {
    slug: "ownership-map", kind: "feature", Icon: GitBranch,
    eyebrow: "Ownership map", title: "Map ownership", highlight: "across complex groups.",
    subhead: "Visualise parent–subsidiary structures, cross-holdings and beneficial ownership chains for any UK company.",
    intro: "Group structures matter. CoSearch builds an interactive ownership graph from PSC and shareholder data, see who owns what, trace control chains through holding companies, and export the map as a PDF for DD packs.",
    image: IMG.legal,
    subFeatures: [
      { Icon: GitBranch, t: "Auto-built tree", d: "Generated from PSC + shareholder data." },
      { Icon: GitBranch, t: "Cross-holdings", d: "Complex Topco/Bidco/Holdco structures supported." },
      { Icon: GitBranch, t: "Beneficial ownership", d: "Resolved through chains of corporate shareholders." },
      { Icon: GitBranch, t: "PDF export", d: "Branded map for DD packs and matter files." },
    ],
    related: ["company-search", "director-search", "sector-reports"],
  },
  {
    slug: "watchlists", kind: "feature", Icon: Bell,
    eyebrow: "Watch lists", title: "Get alerted the moment", highlight: "anything changes.",
    subhead: "Add any UK company to your watch list and get email or webhook alerts within 4 hours of any Companies House change.",
    intro: "Whether it's a new PSC, a changed director, late accounts or a strike-off notice, CoSearch's watch lists notify you within hours of any Companies House change. Configure per-company alert rules and pipe alerts into Slack or your CRM via webhook.",
    image: IMG.desk,
    subFeatures: [
      { Icon: Bell, t: "Per-company rules", d: "Choose which events to watch per company." },
      { Icon: Bell, t: "4-hour SLA", d: "Alert delivered within 4 hours of source change." },
      { Icon: Bell, t: "Email + webhook", d: "Pipe alerts into Slack, Teams or your CRM." },
      { Icon: Bell, t: "Bulk add", d: "Import 500 companies via CSV in one click." },
    ],
    related: ["company-search", "director-search", "api-access"],
  },
  {
    slug: "sector-reports", kind: "feature", Icon: BarChart3,
    eyebrow: "Sector reports", title: "Benchmark any UK", highlight: "company against its peers.",
    subhead: "Sector reports + peer benchmarks for every UK SIC code, accounts size, employee count, growth, geography.",
    intro: "Pick a sector by SIC code and CoSearch generates a peer-group benchmark, median accounts size, growth, geographic distribution, top 50 by turnover. Use it for pitch decks, market sizing and competitor analysis.",
    image: IMG.pe,
    subFeatures: [
      { Icon: BarChart3, t: "SIC peer benchmarks", d: "Median accounts and growth per sector code." },
      { Icon: BarChart3, t: "Top 50 by turnover", d: "Ranked lists per SIC code, exportable." },
      { Icon: BarChart3, t: "Geographic heatmap", d: "Density of companies plotted by registered office." },
      { Icon: BarChart3, t: "Branded PDF reports", d: "White-labelled for client deliverables." },
    ],
    related: ["company-search", "ownership-map", "api-access"],
  },
  {
    slug: "api-access", kind: "feature", Icon: Code2,
    eyebrow: "API access", title: "Embed UK company data", highlight: "anywhere.",
    subhead: "RESTful API with 200k req/day, OpenAPI spec, sandbox keys and full webhook support.",
    intro: "CoSearch ships a RESTful API for every endpoint, company lookup, director search, ownership graphs, watch-list webhooks. Sandbox keys are free; production keys ship with a 99.9% SLA and rate limits suited to enterprise pipelines.",
    image: IMG.laptop,
    subFeatures: [
      { Icon: Code2, t: "REST + OpenAPI", d: "All endpoints documented in OpenAPI 3.1." },
      { Icon: Code2, t: "Webhooks", d: "Event-driven alerts on watch-list changes." },
      { Icon: Code2, t: "Sandbox keys", d: "Free testing tier, no credit card." },
      { Icon: Code2, t: "Enterprise SLA", d: "99.9% uptime on production keys." },
    ],
    related: ["watchlists", "sector-reports", "company-search"],
  },
];

export function findCoSearchFeature(slug) {
  return COSEARCH_FEATURES.find((f) => f.slug === slug) || null;
}
